// maths.portll.net — "Your Data" pack builder (engine entry point).
//
// The one call the UI makes to turn a kid's hand-collected dataset into questions.
// It decides ENOUGH-ness (via datasetShapes.hasEnoughData) and, when there's enough,
// delegates to the pure generators. Below the threshold — or if the shape somehow
// yields no answerable questions — it returns a warm "collect a few more" nudge
// instead of thin or empty content.

import { hasEnoughData, isBetaKind } from "../lib/datasetShapes.js";
import { generatePack } from "./generators.js";

const NOT_ENOUGH = "Collect a few more and I'll have better questions.";

/**
 * @param {{ kind:string, data:object, generatorHints?:string[] }} dataset
 * @returns {{ enough:boolean, questions:import("./generators.js").Question[], reason?:string }}
 */
export function buildYourDataPack(dataset) {
  const kind = dataset?.kind;
  const data = dataset?.data;

  // Only the three beta kinds have generators; two_sample/bivariate land post-beta.
  if (!kind || !isBetaKind(kind)) {
    return { enough: false, questions: [], reason: "Questions for this kind of data are coming soon." };
  }

  if (!hasEnoughData(kind, data)) {
    return { enough: false, questions: [], reason: NOT_ENOUGH };
  }

  const hints = Array.isArray(dataset.generatorHints) ? dataset.generatorHints : undefined;
  const questions = generatePack(kind, data, hints);

  // Enough observations, but every generator's guard tripped (e.g. everything tied)
  // → still nudge rather than render an empty pack.
  if (!questions.length) {
    return { enough: false, questions: [], reason: NOT_ENOUGH };
  }

  return { enough: true, questions };
}

export { generatePack } from "./generators.js";
